import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Scale, FileSearch, ArrowRight, Sparkles, ChevronRight, Download, ArrowLeft, CheckCircle2, Clock, AlertCircle, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

// Funkcja formatująca Markdown na HTML z nowoczesnym stylowaniem
const formatMarkdown = (text) => {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    // Headers
    .replace(/^#### (.+)$/gm, '<h4 class="text-sm font-bold text-slate-700 dark:text-slate-200 mt-5 mb-2">$1</h4>')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-slate-800 dark:text-slate-100 mt-6 mb-3 pb-2 border-b border-slate-200 dark:border-slate-600">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-violet-700 dark:text-violet-300 mt-6 mb-3 pb-2 border-b-2 border-violet-200 dark:border-violet-700">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-extrabold text-slate-900 dark:text-white mt-4 mb-4 pb-3 border-b-2 border-violet-500">$1</h1>')
    // Bold + Italic
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-800 dark:text-white">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="text-slate-600 dark:text-slate-300">$1</em>')
    // Horizontal rule
    .replace(/^---$/gm, '<hr class="my-6 border-t-2 border-slate-200 dark:border-slate-600" />')
    // Lists
    .replace(/^- (.+)$/gm, '<li class="ml-4 pl-2 py-1 text-sm text-slate-700 dark:text-slate-300 relative before:content-[\'•\'] before:absolute before:-left-3 before:text-violet-500 before:font-bold">$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-4 pl-2 py-1 text-sm text-slate-700 dark:text-slate-300 list-decimal">$2</li>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
    // Placeholders [●] and similar
    .replace(/\[●\]/g, '<span class="inline-block w-32 border-b-2 border-dotted border-slate-400 mx-1"></span>')
    .replace(/\.{3,}/g, '<span class="inline-block w-24 border-b border-dotted border-slate-300"></span>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">')
    .replace(/\n/g, '<br/>');

  html = '<p class="mb-4 text-sm leading-relaxed text-slate-600 dark:text-slate-300">' + html + '</p>';
  // Wrap list items in ul
  html = html.replace(/(<li[^>]*>.*?<\/li>)+/gs, '<ul class="space-y-1 my-4 bg-slate-50 dark:bg-slate-700/30 rounded-lg p-4">$&</ul>');
  return html;
};

const TEMPLATE_FIELDS = {
  wezwanie_do_zaplaty: [
    { key: 'dane_dluznika', label: 'Dane dłużnika (nazwa, adres)', type: 'textarea', placeholder: 'np. ABC Sp. z o.o., ul. Przykładowa 1, 00-001 Warszawa' },
    { key: 'nr_faktury', label: 'Numer faktury', type: 'text', placeholder: 'np. FV/2026/001' },
    { key: 'kwota', label: 'Kwota do zapłaty (PLN)', type: 'text', placeholder: 'np. 5 000,00' },
    { key: 'termin_zaplaty', label: 'Pierwotny termin zapłaty', type: 'text', placeholder: 'np. 15.01.2026' },
  ],
  nota_korygujaca: [
    { key: 'nr_faktury_oryginalnej', label: 'Numer faktury oryginalnej', type: 'text', placeholder: 'np. FV/2026/001' },
    { key: 'co_poprawic', label: 'Co wymaga korekty', type: 'textarea', placeholder: 'np. błędny adres nabywcy' },
    { key: 'prawidlowa_tresc', label: 'Prawidłowa treść', type: 'textarea', placeholder: 'np. ul. Nowa 10, 00-002 Warszawa' },
  ],
  korekta_faktury: [
    { key: 'nr_faktury_oryginalnej', label: 'Numer faktury oryginalnej', type: 'text', placeholder: 'np. FV/2026/001' },
    { key: 'przyczyna_korekty', label: 'Przyczyna korekty', type: 'textarea', placeholder: 'np. błędna ilość towaru' },
    { key: 'nowe_kwoty', label: 'Nowe kwoty (netto, VAT, brutto)', type: 'textarea', placeholder: 'np. netto: 1000 zł, VAT 23%: 230 zł, brutto: 1230 zł' },
  ],
  interpretacja_indywidualna: [
    { key: 'opis_stanu_faktycznego', label: 'Opis stanu faktycznego', type: 'textarea', placeholder: 'Opisz szczegółowo sytuację...' },
    { key: 'pytanie', label: 'Pytanie do organu podatkowego', type: 'textarea', placeholder: 'Jakie pytanie chcesz zadać?' },
    { key: 'stanowisko_wnioskodawcy', label: 'Własne stanowisko i podstawa prawna', type: 'textarea', placeholder: 'Twoja interpretacja i przepisy...' },
  ],
  czynny_zal: [
    { key: 'opis_naruszenia', label: 'Opis naruszenia (co i kiedy)', type: 'textarea', placeholder: 'np. niezłożenie deklaracji VAT-7 za styczeń 2026' },
    { key: 'okolicznosci', label: 'Okoliczności', type: 'textarea', placeholder: 'np. przyczyny opóźnienia' },
    { key: 'sposob_naprawienia', label: 'Sposób naprawienia (np. zapłata zaległości)', type: 'textarea', placeholder: 'np. złożono deklarację, zapłacono podatek z odsetkami' },
  ],
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fields, setFields] = useState({});
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/templates/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(res.data.templates);
      } catch { toast.error('Błąd ładowania szablonów'); }
    };
    fetchTemplates();
  }, []);

  const handleGenerate = async () => {
    if (!selected) return;
    setLoading(true);
    setResult('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/templates/generate`, {
        template_id: selected.id,
        fields
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data.document);
      toast.success('Dokument wygenerowany!');
    } catch { toast.error('Błąd generowania dokumentu'); }
    finally { setLoading(false); }
  };

  const handleExportPdf = async () => {
    if (!result) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/export/word`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: result,
          title: selected?.name || 'Dokument',
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `KAI_${selected?.id || 'dokument'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF pobrany!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Błąd eksportu PDF - spróbuj ponownie');
    } finally {
      setExporting(false);
    }
  };

  const categoryIcons = {
    'Windykacja': <Scale className="w-5 h-5" />,
    'Faktury': <FileText className="w-5 h-5" />,
    'Urzędowe': <FileSearch className="w-5 h-5" />,
  };

  const categoryColors = {
    'Windykacja': 'from-red-500 to-rose-600',
    'Faktury': 'from-blue-500 to-indigo-600',
    'Urzędowe': 'from-violet-500 to-purple-600',
  };

  const categoryBg = {
    'Windykacja': 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800',
    'Faktury': 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800',
    'Urzędowe': 'bg-violet-50 dark:bg-violet-900/20 border-violet-100 dark:border-violet-800',
  };

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Modern Header */}
      <div className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')} 
              data-testid="back-to-dashboard"
              className="gap-2 text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="w-4 h-4" /> Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200 dark:shadow-none">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-white">Szablony dokumentów</h1>
                <p className="text-xs text-slate-400">Generuj profesjonalne pisma z AI</p>
              </div>
            </div>
          </div>
          {result && (
            <Button 
              size="sm" 
              onClick={handleExportPdf} 
              disabled={exporting}
              data-testid="export-template-pdf" 
              className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white shadow-lg shadow-emerald-200 dark:shadow-none gap-2"
            >
              {exporting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Eksportowanie...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" /> Pobierz PDF
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 lg:p-8">
        {!selected ? (
          /* Template Selection - Modern Grid */
          <div>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Wybierz szablon dokumentu</h2>
              <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
                Wypełnij formularz danymi, a sztuczna inteligencja wygeneruje profesjonalny dokument zgodny z przepisami prawa.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {templates.map(t => (
                <div
                  key={t.id}
                  data-testid={`template-card-${t.id}`}
                  onClick={() => { setSelected(t); setFields({}); setResult(''); }}
                  className={`group relative p-6 bg-white dark:bg-slate-800 rounded-2xl border-2 ${categoryBg[t.category] || 'bg-slate-50 border-slate-100'} hover:shadow-xl hover:scale-[1.02] cursor-pointer transition-all duration-300`}
                >
                  {/* Category Badge */}
                  <div className="absolute top-4 right-4">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gradient-to-r ${categoryColors[t.category] || 'from-slate-400 to-slate-500'} text-white`}>
                      {t.category}
                    </span>
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${categoryColors[t.category] || 'from-slate-400 to-slate-500'} flex items-center justify-center text-white mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                    {categoryIcons[t.category] || <FileText className="w-6 h-6" />}
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-bold text-lg text-slate-800 dark:text-white mb-2 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">{t.name}</h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 line-clamp-2">{t.description}</p>
                  
                  {/* CTA */}
                  <div className="flex items-center text-sm font-semibold text-violet-600 dark:text-violet-400 group-hover:gap-3 transition-all">
                    Rozpocznij <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
            
            {/* Info Banner */}
            <div className="mt-8 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 dark:text-white mb-1">Jak to działa?</h4>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Wybierz szablon, wypełnij wymagane pola i kliknij "Wygeneruj". AI przygotuje profesjonalny dokument z właściwą podstawą prawną i formatowaniem. Możesz go pobrać jako PDF.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Template Form + Result - Modern Layout */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Form */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {/* Form Header */}
              <div className={`p-5 bg-gradient-to-r ${categoryColors[selected.category] || 'from-slate-400 to-slate-500'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                      {categoryIcons[selected.category] || <FileText className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h2 className="font-bold text-white text-lg">{selected.name}</h2>
                      <p className="text-white/80 text-sm">{selected.description}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Form Body */}
              <div className="p-5">
                <div className="flex justify-end mb-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setSelected(null); setResult(''); }}
                    data-testid="back-to-templates"
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <ArrowLeft className="w-3 h-3 mr-1" /> Zmień szablon
                  </Button>
                </div>
                
                <div className="space-y-5">
                  {(TEMPLATE_FIELDS[selected.id] || []).map((f, idx) => (
                    <div key={f.key} className="group">
                      <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        <span className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-500">{idx + 1}</span>
                        {f.label}
                      </label>
                      {f.type === 'textarea' ? (
                        <textarea
                          data-testid={`field-${f.key}`}
                          value={fields[f.key] || ''}
                          onChange={e => setFields({ ...fields, [f.key]: e.target.value })}
                          className="w-full p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm resize-none h-28 focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all"
                          placeholder={f.placeholder || `Wpisz ${f.label.toLowerCase()}...`}
                        />
                      ) : (
                        <input
                          data-testid={`field-${f.key}`}
                          type="text"
                          value={fields[f.key] || ''}
                          onChange={e => setFields({ ...fields, [f.key]: e.target.value })}
                          className="w-full p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all"
                          placeholder={f.placeholder || `Wpisz ${f.label.toLowerCase()}...`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                
                <Button
                  data-testid="generate-template-btn"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-full mt-6 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white h-12 rounded-xl shadow-lg shadow-violet-200 dark:shadow-none text-base font-semibold"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generowanie dokumentu...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2"><Sparkles className="w-5 h-5" /> Wygeneruj dokument</span>
                  )}
                </Button>
              </div>
            </div>

            {/* Right: Result */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              {/* Result Header */}
              <div className="p-5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${result ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-700'}`}>
                    {result ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 dark:text-white">
                      {result ? 'Dokument gotowy!' : 'Podgląd dokumentu'}
                    </h3>
                    <p className="text-xs text-slate-400">
                      {result ? 'Możesz pobrać jako PDF' : 'Wypełnij formularz i wygeneruj'}
                    </p>
                  </div>
                </div>
                {result && (
                  <Button 
                    size="sm"
                    onClick={handleExportPdf}
                    disabled={exporting}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
                  >
                    {exporting ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    PDF
                  </Button>
                )}
              </div>
              
              {/* Result Content */}
              {result ? (
                <ScrollArea className="h-[550px]">
                  <div className="p-6">
                    {/* Document Preview Container */}
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                      {/* Document Header */}
                      <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-bold text-white">{selected?.name || 'Dokument'}</h4>
                            <p className="text-xs text-slate-300">Wygenerowano: {new Date().toLocaleDateString('pl-PL')}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Document Body */}
                      <div 
                        data-testid="template-result"
                        className="p-8 prose-document"
                        dangerouslySetInnerHTML={{ __html: formatMarkdown(result) }}
                      />
                      
                      {/* Document Footer */}
                      <div className="px-6 py-4 bg-slate-50 dark:bg-slate-700/50 border-t border-slate-200 dark:border-slate-600">
                        <p className="text-xs text-slate-400 text-center">
                          Dokument wygenerowany przez KAI - Asystent Księgowy AI | kaidoradza.pl
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-[500px] flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-10 h-10 text-slate-300 dark:text-slate-500" />
                    </div>
                    <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Brak dokumentu</h4>
                    <p className="text-sm text-slate-400 max-w-xs">
                      Wypełnij formularz po lewej stronie i kliknij "Wygeneruj dokument" aby zobaczyć podgląd
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
